import React, { ChangeEvent, useEffect, useMemo, useState } from "react";

import { LoadingIcon, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import config from "console_config";
import { ButtonWrapper, Button, Link } from "ds/Button";
import Input from "ds/Input";
import Modal from "ds/Modal";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize, checkGoLive, interpolateURL } from "Libs/utils";
import { environmentSelector, loadEnvironment } from "Reducers/environment";
import { organizationSelector } from "Reducers/organization";
import { loadingSelector } from "Reducers/organization/subscription";
import { loadOneProject, gitProjectSelector } from "Reducers/project/project";
import { regionLabelSelector } from "Reducers/project/region";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./styles";

import type { OrganizationSubscription, Subscription } from "@packages/client";

type DeleteSubscriptionModalPropType = {
  subscription: Subscription | OrganizationSubscription;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (event: any) => void;
};

const DeleteSubscriptionModal = (prop: DeleteSubscriptionModalPropType) => {
  const { subscription, isOpen, onClose, onDelete } = prop;

  const formatMessage = useIntl().formatMessage;
  const [confirmSubscriptionName, setConfirmSubscriptionName] = useState("");
  const dispatch = useAppDispatch();

  const isLoadingSubscription = useAppSelector(loadingSelector);

  const { project_title: projectTitle, owner, project_id } = subscription;

  const organization = useAppSelector(state =>
    organizationSelector(state, { organizationId: owner })
  );

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId: organization?.name,
      projectId: project_id
    })
  );

  let environmentId: string = "",
    organizationId: string = "",
    projectId: string = "";

  if (project?.id) {
    environmentId = project?.default_branch;
    organizationId = organization?.name ?? "";
    projectId = project.id;
  }

  const mainEnv = useSelectorWithUrlParams(environmentSelector, {
    environmentId,
    organizationId,
    projectId
  });
  const regionLabel = useSelectorWithUrlParams(regionLabelSelector, {
    organizationId,
    projectId
  });

  useEffect(() => {
    if (environmentId && organizationId && projectId) {
      dispatch(loadEnvironment({ organizationId, projectId, environmentId }));
    }
  }, [dispatch, environmentId, organizationId, project, projectId]);

  useEffect(() => {
    project_id &&
      dispatch(
        loadOneProject({
          projectId: project_id,
          organizationId,
          loadActivitiesAndSubscribe: false
        })
      );
  }, [dispatch, project_id, organizationId]);

  const mainUrl = useMemo(() => {
    const url = project?.default_domain || mainEnv?.edge_hostname;
    if (url) return `https://${url}`;
  }, [mainEnv, project]);

  const allowDelete = !!(
    projectTitle && confirmSubscriptionName === projectTitle.trim()
  );

  const handleDelete = (event: any) => {
    event.preventDefault();
    if (
      config.CUSTOM_URL_SUBSCRIPTION_DELETE_REDIRECT &&
      typeof config.CUSTOM_URL_SUBSCRIPTION_DELETE_REDIRECT === "string"
    ) {
      window.location.href = interpolateURL(
        config.CUSTOM_URL_SUBSCRIPTION_DELETE_REDIRECT,
        { projectId }
      );
    }
    onDelete(event);
  };

  return (
    <Modal isOpen={isOpen} size="medium" onOpenChange={onClose}>
      <Typography.Title tag="h3">
        {formatMessage({
          id: "settings.plan.project.modal.title",
          defaultMessage: "Delete project"
        })}
      </Typography.Title>
      {subscription?.id ? (
        <>
          <div>
            <Typography.BodyText size="medium" weight="regular">
              {formatMessage(
                {
                  id: "settings.plan.project.delete.confirm"
                },
                {
                  projectTitle,
                  strong: txt => <strong>{txt}</strong>,
                  br: <br />
                }
              )}
            </Typography.BodyText>
          </div>
          <S.ProjectTitle weight="bold">{projectTitle}</S.ProjectTitle>

          <Typography.Label $color="slate" textStyle="lowercaseControl">
            {organization?.label}
            {"  "}
            {regionLabel?.title}
            {regionLabel?.suffix && `[${regionLabel.suffix}]`}
          </Typography.Label>

          {mainUrl && (
            <S.LinkWrapper>
              <Link href={mainUrl} analyticId={mainUrl}>
                {mainUrl}
              </Link>
              {checkGoLive(project, organization) === "live" && (
                <S.LiveTag>{formatMessage({ id: "live" })}</S.LiveTag>
              )}
            </S.LinkWrapper>
          )}

          <Input
            analyticId="subscription.input.delete.confirm-name-label"
            id="confirm-input"
            name="confirm-input"
            label={formatMessage({
              id: "subscription.input.delete.confirm-name-label"
            })}
            placeholder={formatMessage({
              id: "project.settings.basic.form.name"
            })}
            required={true}
            value={confirmSubscriptionName}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setConfirmSubscriptionName(event.target.value)
            }
            width="264px"
          />
          <S.WarningWrapper>
            <div>
              <S.Warning />
            </div>
            <S.CautionText>
              {formatMessage({ id: "subscription.delete.caution" })}
            </S.CautionText>
          </S.WarningWrapper>

          <ButtonWrapper spacing="modal" justifyContent="end">
            {isLoadingSubscription !== "idle" && isLoadingSubscription ? (
              <LoadingIcon />
            ) : (
              <>
                <Button
                  analyticId="cancel"
                  variant="secondary"
                  aria-label={formatMessage({ id: "cancel" })}
                  onClick={onClose}
                >
                  {capitalize(formatMessage({ id: "cancel" }))}
                </Button>
                <Button
                  analyticId="subscription.button.delete"
                  data-testid="confirm-delete-button"
                  disabled={!allowDelete}
                  aria-label={capitalize(formatMessage({ id: "delete" }))}
                  onClick={handleDelete}
                >
                  {capitalize(
                    formatMessage({ id: "subscription.button.delete" })
                  )}
                </Button>
              </>
            )}
          </ButtonWrapper>
        </>
      ) : (
        <S.LoadingWrapper>
          <LoadingIcon />
        </S.LoadingWrapper>
      )}
    </Modal>
  );
};

export default DeleteSubscriptionModal;
